import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Workflow, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "@/lib/api";

/**
 * EnrollSequenceDialog — pick one of the user's sequences and enroll the
 * given lead. Subsequent steps fire automatically via the backend scheduler.
 */
export default function EnrollSequenceDialog({ open, onClose, lead }) {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smtpReady, setSmtpReady] = useState(null);
  const [chosen, setChosen] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const [activeEnrollment, setActiveEnrollment] = useState(null);
  const [cooldown, setCooldown] = useState(null);

  useEffect(() => {
    if (!open) return;
    setChosen("");
    setLoading(true);
    setCooldown(null);
    Promise.all([
      http.get("/sequences").then((r) => r.data.sequences || []).catch(() => []),
      http.get("/outreach/smtp").then((r) => r.data.config).catch(() => null),
      http
        .get(`/leads/${lead?.id}/enrollment`)
        .then((r) => ({ enr: r.data?.active ? r.data.enrollment : null, cooldown: r.data?.cooldown || null }))
        .catch(() => ({ enr: null, cooldown: null })),
    ]).then(([seq, smtp, leadState]) => {
      setSequences(seq);
      setSmtpReady(!!smtp);
      setActiveEnrollment(leadState.enr);
      setCooldown(leadState.cooldown);
      const firstUsable = seq.find((s) => s.status !== "archived" && s.steps_count > 0);
      if (firstUsable) setChosen(firstUsable.id);
      setLoading(false);
    });
  }, [open, lead?.id]);

  const onStopExisting = async () => {
    if (!activeEnrollment) return;
    if (
      !window.confirm(
        `Stop the current "${activeEnrollment.sequence_name}" sequence for this lead? The lead will exit immediately and become available for a new sequence.`,
      )
    ) {
      return;
    }
    try {
      await http.post(`/enrollments/${activeEnrollment.id}/stop`);
      toast.success("Sequence stopped — you can enroll in a new one now.");
      setActiveEnrollment(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't stop sequence");
    }
  };

  const onEnroll = async () => {
    if (!chosen || !lead?.id) return;
    setEnrolling(true);
    try {
      const { data } = await http.post(`/sequences/${chosen}/enroll`, {
        lead_ids: [lead.id],
      });
      if (data.enrolled) {
        toast.success("Enrolled — first step fires within 5 minutes");
        onClose?.();
      } else {
        // backend rejected (race condition — someone else just enrolled it)
        const r = (data.skipped_reasons || [])[0];
        if (r?.reason === "already_in_sequence") {
          toast.warning(`Lead is already in "${r.sequence_name}". Stop that sequence first.`);
          setActiveEnrollment({
            id: null,
            sequence_id: r.sequence_id,
            sequence_name: r.sequence_name,
            status: r.status,
          });
        } else if (r?.reason === "in_cooldown") {
          toast.warning(`Lead is in cooldown — recently exited a sequence.`);
          setCooldown({
            until: r.cooldown_until,
            days: r.cooldown_days,
            exit_reason: r.exit_reason,
            exited_at: r.exited_at,
          });
        } else {
          toast.warning("Lead could not be enrolled (skipped)");
        }
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not enroll");
    } finally {
      setEnrolling(false);
    }
  };

  const usable = sequences.filter((s) => s.status !== "archived" && s.steps_count > 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md rounded-sm" data-testid="enroll-sequence-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold flex items-center gap-2">
            <Workflow className="w-5 h-5 text-brand" />
            Enroll in sequence
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="grid place-items-center py-10 text-ink-400 text-sm">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : cooldown ? (
          <div className="space-y-4 py-2" data-testid="enroll-in-cooldown">
            <div className="bg-[#FFF8E5] border border-[#F0C36D] rounded-sm p-4 flex items-start gap-3">
              <Workflow className="w-5 h-5 text-[#B27A00] shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-ink-900 text-sm">
                  Lead is in cooldown
                </div>
                <div className="text-ink-700 text-[12px] leading-relaxed mt-1">
                  This lead recently exited a sequence
                  {cooldown.exit_reason ? ` (${cooldown.exit_reason})` : ""}. To prevent
                  flooding fresh exits, re-enrollment is blocked until{" "}
                  <strong>
                    {cooldown.until ? new Date(cooldown.until).toLocaleDateString(undefined, {
                      year: "numeric", month: "short", day: "numeric",
                    }) : "the cooldown ends"}
                  </strong>{" "}
                  (your cooldown setting: {cooldown.days} days).
                </div>
              </div>
            </div>
            <p className="text-ink-500 text-[12px] leading-relaxed">
              You can shorten or disable the cooldown window in <strong>Outreach → Setup → Cooldown</strong>.
            </p>
            <Button asChild variant="outline" className="rounded-sm w-full" data-testid="cooldown-goto-setup">
              <Link to="/settings/outreach">Adjust cooldown setting</Link>
            </Button>
          </div>
        ) : activeEnrollment ? (
          <div className="space-y-4 py-2" data-testid="enroll-already-active">
            <div className="bg-[#FFF8E5] border border-[#F0C36D] rounded-sm p-4 flex items-start gap-3">
              <Workflow className="w-5 h-5 text-[#B27A00] shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-ink-900 text-sm">
                  Already enrolled in "{activeEnrollment.sequence_name}"
                </div>
                <div className="text-ink-700 text-[12px] leading-relaxed mt-1">
                  This lead is currently <strong>{activeEnrollment.status}</strong> on step{" "}
                  <strong>{(activeEnrollment.current_step_index ?? 0) + 1}</strong>. A prospect can
                  only be in one active sequence at a time to avoid parallel email streams.
                </div>
              </div>
            </div>
            <p className="text-ink-500 text-[12px] leading-relaxed">
              Stop the current sequence first if you want to enroll the lead in a different one.
            </p>
            <div className="flex gap-2">
              <Button asChild variant="outline" className="rounded-sm flex-1" data-testid="view-current-sequence">
                <Link to="/settings/outreach">
                  View sequences
                </Link>
              </Button>
              {activeEnrollment.id && (
                <Button
                  onClick={onStopExisting}
                  className="rounded-sm flex-1 bg-ink-900 hover:bg-ink-800 text-white"
                  data-testid="stop-current-sequence"
                >
                  Stop &amp; re-enroll
                </Button>
              )}
            </div>
          </div>
        ) : !smtpReady ? (
          <div className="space-y-3 py-2">
            <p className="text-ink-700 text-sm">
              Connect SMTP first so the scheduler can send emails on your behalf.
            </p>
            <Button asChild className="rounded-sm w-full">
              <Link to="/settings/outreach">
                Set up SMTP <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        ) : usable.length === 0 ? (
          <div className="space-y-3 py-2">
            <p className="text-ink-700 text-sm">
              You don't have any active sequences with steps yet. Create one first
              from the Outreach → Sequences tab.
            </p>
            <Button asChild className="rounded-sm w-full" data-testid="goto-sequences-tab">
              <Link to="/settings/outreach">
                Open Sequences <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-ink-500 text-sm">
              {lead?.input?.company || "This lead"} will receive step 1 within 5
              minutes. Replies auto-pause the sequence.
            </p>
            <div
              className="space-y-2 max-h-72 overflow-y-auto"
              data-testid="enroll-sequence-options"
            >
              {usable.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setChosen(s.id)}
                  data-testid={`enroll-option-${s.id}`}
                  className={`w-full border rounded-sm p-3 text-left transition-colors ${
                    chosen === s.id
                      ? "border-ink-900 bg-ink-50"
                      : "border-ink-100 hover:border-ink-300"
                  }`}
                >
                  <div className="font-semibold text-ink-900">{s.name}</div>
                  <div className="text-[11px] uppercase tracking-widestx font-mono text-ink-400 mt-0.5">
                    {s.status} · {s.steps_count} steps
                  </div>
                </button>
              ))}
            </div>
            <Button
              onClick={onEnroll}
              disabled={!chosen || enrolling}
              className="rounded-sm w-full"
              data-testid="confirm-enroll-btn"
            >
              {enrolling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Enroll lead
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
